import { House, Link, Pin, type LucideIcon } from "lucide-react";
import { createElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { bounds, buildingPoints, DEFAULT_STOREY, gridBlocks, gridOutline, pathGrid, polygonGrid, STATUS_LABEL, type BlockGrid, type PlanObject } from "@mcp/plan";
import type { HeightField } from "../engine/HeightField.ts";
import { createPlanTextureMaterial, type SharedUniforms } from "../engine/materials.ts";
import type { Peer } from "./PlanSession.ts";

const OPACITY: Record<PlanObject["status"], number> = { idea: 0.3, planned: 0.5, in_progress: 0.65, done: 0.8 };
/** textured objects are drawn more solid so the block is recognisable */
const TEX_OPACITY: Record<PlanObject["status"], number> = { idea: 0.5, planned: 0.72, in_progress: 0.82, done: 0.92 };

/** What the renderer needs to know about a block material (from @mcp/assets blockMaterial). */
export interface MaterialInfo {
  top: number; side: number; tintTop: number | null; tintSide: number | null;
  /** texture shown where the top texture is see-through (farmland under crops), or null */
  under?: number | null;
}

/** invisible box so a schematic building (drawn by StructureRenderer) can be clicked and dragged */
const PICK_MAT = new THREE.MeshBasicMaterial({ visible: false });

/** none · part of a multi-selection · the (single) selection that shows edit handles */
type SelMode = 0 | 1 | 2;

export interface Selection {
  ids: string[];
  primary: string | null;
}

interface Entry {
  obj: PlanObject;
  group: THREE.Group;
  sel: SelMode;
  label: HTMLDivElement;
}

/** Turns plan objects into three.js geometry draped over the terrain height field. */
/** a lucide icon as SVG markup, for the name tags this renderer builds without React (rendered once, at load) */
function iconSvg(icon: LucideIcon): string {
  const el = document.createElement("span");
  const root = createRoot(el);
  flushSync(() => root.render(createElement(icon, { size: 11, "aria-hidden": true })));
  const svg = el.innerHTML;
  root.unmount();
  return svg;
}
const ICON_SVG = { link: iconSvg(Link), pin: iconSvg(Pin), home: iconSvg(House) };

export class PlanRenderer {
  private entries = new Map<string, Entry>();
  private root: THREE.Group;
  private heights: HeightField;
  readonly draft = new THREE.Group();
  private peers = new THREE.Group();
  private home = new THREE.Group();
  /** ghost of a layout / template before it's applied */
  private preview = new THREE.Group();
  showLabels = true;
  /** outline / centre / storey lines of objects (the selected object always shows them) */
  showEdges = true;
  dimension = "minecraft:overworld";
  /** label interactions (wired up by PlanTools) */
  onLabelPointerDown: (id: string, e: PointerEvent) => void = () => {};
  onLabelWheel: (e: WheelEvent) => void = () => {};
  onLabelDblClick: (id: string) => void = () => {};
  /** the 📌 badge on a name tag was clicked */
  onBadgeClick: (id: string) => void = () => {};
  /** a schematic building's blocks are drawn (its file is on this computer) → only outline + label here */
  structureShown: (o: PlanObject) => boolean = () => false;
  private boards = new Map<string, { count: number; image?: string }>();

  /** pin count + first image per object (shown on the name tags; hover shows the image) */
  setBoards(b: Map<string, { count: number; image?: string }>) {
    this.boards = b;
    for (const [id, e] of this.entries) if (this.renaming?.id !== id) this.applyBadge(e.label, id);
  }

  /** link icon on the name tag: opens the object's source link (e.g. the original post of a schematic) */
  private applyLink(div: HTMLDivElement, o: PlanObject) {
    if (!o.sourceUrl) return;
    const a = document.createElement("a");
    a.className = "src-link";
    a.href = o.sourceUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.innerHTML = ICON_SVG.link;
    a.setAttribute("aria-label", "Open the source");
    a.title = `Open the source: ${o.sourceUrl}`;
    a.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    a.addEventListener("dblclick", (ev) => ev.stopPropagation());
    div.appendChild(a);
  }

  private applyBadge(div: HTMLDivElement, id: string) {
    div.querySelector(".pin-badge")?.remove();
    div.querySelector(".pin-thumb")?.remove();
    const b = this.boards.get(id);
    if (!b?.count) return;
    const badge = document.createElement("span");
    badge.className = "pin-badge";
    badge.innerHTML = ICON_SVG.pin;
    badge.append(` ${b.count}`);
    badge.title = "Open the pinboard";
    badge.addEventListener("pointerdown", (ev) => { ev.stopPropagation(); ev.preventDefault(); this.onBadgeClick(id); });
    div.appendChild(badge);
    if (b.image) {
      const img = document.createElement("img");
      img.className = "pin-thumb";
      img.src = b.image;
      img.alt = "";
      div.appendChild(img);
    }
  }
  private renaming: { id: string; input: HTMLInputElement } | null = null;
  /** block texture array + material lookup (set when game assets are loaded) */
  private tex: SharedUniforms["uTex"] | null = null;
  private materialInfo: (id: string) => MaterialInfo | null = () => null;

  setMaterialSource(tex: SharedUniforms["uTex"], lookup: (id: string) => MaterialInfo | null) {
    this.tex = tex;
    this.materialInfo = lookup;
    this.rebuildAll();
  }

  /** textured material for an object's top (paths, areas, roofs) or side (walls), or null = plain colour */
  private texturedMat(o: PlanObject, face: "top" | "side", selected: boolean): THREE.Material | null {
    if (!o.material || !this.tex) return null;
    const m = this.materialInfo(o.material);
    if (!m) return null;
    const tint = face === "top" ? m.tintTop : m.tintSide;
    const op = Math.min(0.97, TEX_OPACITY[o.status] + (selected ? 0.05 : 0));
    return createPlanTextureMaterial(this.tex, face === "top" ? m.top : m.side, new THREE.Color(tint ?? 0xffffff), new THREE.Color(o.color), op, face === "top" && o.kind !== "building" ? m.under ?? -1 : -1);
  }

  constructor(root: THREE.Group, heights: HeightField) {
    this.root = root;
    this.heights = heights;
    root.add(this.draft, this.peers, this.home, this.preview);
  }

  private lastSel: Selection = { ids: [], primary: null };

  sync(objects: PlanObject[], selection: Selection) {
    this.lastSel = selection;
    const ids = new Set(selection.ids);
    const seen = new Set<string>();
    for (const o of objects) {
      if (o.dimension !== this.dimension) continue;
      seen.add(o.id);
      const e = this.entries.get(o.id);
      const sel: SelMode = !ids.has(o.id) ? 0 : ids.size === 1 && o.id === selection.primary ? 2 : 1;
      if (this.renaming?.id === o.id && e) continue; // don't destroy the inline editor
      if (e && e.obj.updatedAt === o.updatedAt && e.sel === sel && JSON.stringify(e.obj) === JSON.stringify(o)) continue;
      if (e) dispose(e.group);
      const { group, label } = this.build(o, sel);
      this.root.add(group);
      this.entries.set(o.id, { obj: o, group, sel, label });
    }
    for (const [id, e] of this.entries) if (!seen.has(id)) { dispose(e.group); this.entries.delete(id); }
  }

  /** Terrain heights arrived/changed: re-drape everything. */
  rebuildAll(selection: Selection = this.lastSel) {
    const objs = [...this.entries.values()].map((e) => e.obj);
    for (const e of this.entries.values()) dispose(e.group);
    this.entries.clear();
    this.sync(objs, selection);
  }

  /** Turn an object's 3D name tag into a text field. Enter/blur saves, Esc cancels. */
  startRename(id: string, commit: (name: string) => void) {
    const e = this.entries.get(id);
    if (!e || this.renaming) return false;
    const div = e.label;
    const input = document.createElement("input");
    input.className = "plan-label-input";
    input.value = e.obj.name;
    input.maxLength = 80;
    div.replaceChildren(input);
    div.classList.add("editing");
    this.renaming = { id, input };
    let done = false;
    const onBlur = () => finish(true);
    const finish = (save: boolean) => {
      if (done) return;
      done = true;
      input.removeEventListener("blur", onBlur);
      const v = input.value.trim();
      this.renaming = null;
      // put the plain tag back; the store update (if any) then rebuilds it with the new name
      div.classList.remove("editing");
      div.textContent = save && v ? v : e.obj.name;
      this.applyBadge(div, id);
      if (save && v && v !== e.obj.name) commit(v);
    };
    input.addEventListener("keydown", (ev) => {
      ev.stopPropagation();
      if (ev.key === "Enter") finish(true);
      else if (ev.key === "Escape") finish(false);
    });
    input.addEventListener("blur", onBlur);
    input.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    setTimeout(() => { input.focus(); input.select(); }, 0);
    return true;
  }

  setEdgesVisible(v: boolean) {
    this.showEdges = v;
    for (const e of this.entries.values()) e.group.traverse((c) => { if (c.userData.edge) c.visible = v || e.sel > 0; });
  }

  setLabelsVisible(v: boolean) {
    this.showLabels = v;
    this.root.traverse((o) => { if (o instanceof CSS2DObject && !o.userData.peer) o.visible = v; });
  }

  private h(x: number, z: number, fb = 64) {
    return this.heights.atOr(x, z, fb);
  }

  build(o: PlanObject, sel: SelMode): { group: THREE.Group; label: HTMLDivElement } {
    const selected = sel > 0;
    const g = new THREE.Group();
    g.userData.planId = o.id;
    const color = new THREE.Color(o.color);
    const op = Math.min(0.95, OPACITY[o.status] + (selected ? 0.15 : 0));
    const fill = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: op, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    const line = new THREE.LineBasicMaterial({ color: selected ? 0xffffff : color, transparent: true, opacity: 0.95, depthTest: false });
    const pts = buildingPoints(o);
    let labelPos = new THREE.Vector3();
    const topMat = this.texturedMat(o, "top", selected) ?? fill;
    const sideMat = this.texturedMat(o, "side", selected) ?? fill;

    if (o.kind === "path" && pts.length >= 1) {
      // rasterise the path into the block columns you'd actually pave
      const cells = gridBlocks(pathGrid(pts, Math.max(1, o.width)), 80000);
      g.add(this.cellMesh(cells, topMat, 0.06));
      const samples = samplePolyline(pts.map(([x, z]) => [x + 0.5, z + 0.5]), 0.5);
      const cl = samples.map(([x, z]) => new THREE.Vector3(x, this.h(x, z) + 0.15, z));
      if (cl.length > 1) g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(cl), line));
      labelPos = (cl[Math.floor(cl.length / 2)] ?? new THREE.Vector3(pts[0][0], this.h(pts[0][0], pts[0][1]), pts[0][1])).clone().add(new THREE.Vector3(0, 1.5, 0));
    } else if (o.kind === "area" && pts.length >= 3) {
      const cells = gridBlocks(polygonGrid(pts), 80000);
      g.add(this.cellMesh(cells, topMat, 0.05));
      let sy = 0;
      for (const [x, z] of cells) sy += this.h(x, z);
      const n = cells.length;
      const ring = samplePolyline([...pts, pts[0]].map(([x, z]) => [x + 0.5, z + 0.5]), 0.5).map(([x, z]) => new THREE.Vector3(x, this.h(x, z) + 0.15, z));
      g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ring), line));
      const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length + 0.5, cz = pts.reduce((a, p) => a + p[1], 0) / pts.length + 0.5;
      labelPos = new THREE.Vector3(cx, n ? sy / n + 2 : this.h(cx, cz) + 2, cz);
    } else if (o.kind === "building" && pts.length >= 3 && o.schematic) {
      // schematic: the blocks come from StructureRenderer; here only outline, pick box and label
      // (or a plain box when the file isn't on this computer)
      const hgt = Math.max(1, o.schematic.size[1]);
      const b = bounds(pts);
      if (this.structureShown(o)) {
        this.buildingShape(g, pts, o.y, hgt, 0, null, line, null);
        const box = new THREE.Mesh(new THREE.BoxGeometry(b.x1 - b.x0 + 1, hgt, b.z1 - b.z0 + 1), PICK_MAT);
        box.position.set((b.x0 + b.x1 + 1) / 2, o.y + hgt / 2, (b.z0 + b.z1 + 1) / 2);
        g.add(box);
      } else {
        this.buildingShape(g, pts, o.y, hgt, 0, { wall: sideMat, roof: topMat }, line, null);
      }
      labelPos = new THREE.Vector3(b.cx + 0.5, o.y + hgt + 1.5, b.cz + 0.5);
    } else if (o.kind === "building" && pts.length >= 3) {
      const hgt = Math.max(1, o.height);
      this.buildingShape(g, pts, o.y, hgt, o.storey ?? DEFAULT_STOREY, { wall: sideMat, roof: topMat }, line, new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.5 }));
      const b = bounds(pts);
      labelPos = new THREE.Vector3(b.cx + 0.5, o.y + hgt + 1.5, b.cz + 0.5);
    } else if (o.kind === "marker" && pts.length >= 1) {
      const [x, z] = pts[0];
      const y = o.y;
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 6, 6), new THREE.MeshBasicMaterial({ color }));
      pole.position.set(x + 0.5, y + 3, z + 0.5);
      const head = new THREE.Mesh(new THREE.OctahedronGeometry(1.4), new THREE.MeshBasicMaterial({ color }));
      head.position.set(x + 0.5, y + 7, z + 0.5);
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.8, 1.3, 24).rotateX(-Math.PI / 2), fill);
      ring.position.set(x + 0.5, y + 0.08, z + 0.5);
      g.add(pole, head, ring);
      labelPos = new THREE.Vector3(x + 0.5, y + 9.5, z + 0.5);
    }

    // label
    const div = document.createElement("div");
    // tags of objects in a layout group only show when zoomed in (hundreds of "House 12" tags otherwise)
    div.className = `plan-label${selected ? " selected" : ""}${o.group && sel !== 2 ? " minor" : ""}`;
    div.title = "Click to select · double-click to rename";
    // the tag acts as a handle for its object: presses/drags/wheel go to the 3D view on its behalf
    div.addEventListener("pointerdown", (ev) => { if (div.classList.contains("editing")) return; ev.preventDefault(); this.onLabelPointerDown(o.id, ev); });
    div.addEventListener("wheel", (ev) => { ev.preventDefault(); this.onLabelWheel(ev); }, { passive: false });
    div.addEventListener("dblclick", (ev) => { ev.stopPropagation(); ev.preventDefault(); this.onLabelDblClick(o.id); });
    div.style.setProperty("--c", o.color);
    div.textContent = o.name;
    if (o.status !== "planned") {
      const s = document.createElement("span");
      s.textContent = STATUS_LABEL[o.status];
      div.appendChild(s);
    }
    if (o.schematic && !this.structureShown(o)) {
      const s = document.createElement("span");
      s.className = "schem-missing";
      s.textContent = "schematic file needed";
      div.appendChild(s);
    }
    this.applyLink(div, o);
    this.applyBadge(div, o.id);
    const label = new CSS2DObject(div);
    label.position.copy(labelPos);
    label.visible = this.showLabels;
    g.add(label);

    // edit handles (single selection only; a schematic keeps its size — move/turn it instead)
    if (sel === 2 && !o.schematic) {
      const hm = new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true, opacity: 0.95 });
      const hg = new THREE.BoxGeometry(0.7, 0.7, 0.7);
      pts.forEach(([x, z], i) => {
        const y = o.kind === "building" || o.kind === "marker" ? o.y : this.h(x, z);
        const m = new THREE.Mesh(hg, hm);
        m.position.set(x + 0.5, y + 0.4, z + 0.5);
        m.renderOrder = 10;
        m.userData = { handle: i, planId: o.id };
        g.add(m);
      });
    }
    g.traverse((c) => {
      if (!c.userData.planId) c.userData.planId = o.id;
      if (c instanceof THREE.Line) { c.userData.edge = true; c.visible = this.showEdges || sel > 0; }
    });
    return { group: g, label: div };
  }

  /**
   * Extruded footprint: walls along the block edges of the covered columns (so angled walls
   * show as the staircase you'd actually build), a roof, outline edges and storey lines.
   */
  private buildingShape(
    g: THREE.Group, pts: [number, number][], y: number, h: number, storey: number,
    fill: { wall: THREE.Material; roof: THREE.Material } | null, line: THREE.LineBasicMaterial | THREE.LineDashedMaterial, storeyMat: THREE.Material | null,
  ) {
    let grid: BlockGrid | null = polygonGrid(pts);
    if (!grid) {
      // enormous footprint: fall back to its bounding box
      const b = bounds(pts);
      grid = { x0: b.x0, z0: b.z0, w: 1, d: 1, cells: new Uint8Array([1]), count: 1 };
      const segs: [number, number, number, number][] = [[b.x0, b.z0, b.x1 + 1, b.z0], [b.x1 + 1, b.z0, b.x1 + 1, b.z1 + 1], [b.x1 + 1, b.z1 + 1, b.x0, b.z1 + 1], [b.x0, b.z1 + 1, b.x0, b.z0]];
      return this.extrude(g, segs, [[b.x0, b.z0, b.x1 + 1, b.z1 + 1]], y, h, storey, fill, line, storeyMat);
    }
    const segs = gridOutline(grid);
    const roof: [number, number, number, number][] = [];
    for (let z = 0; z < grid.d; z++) {
      let start = -1;
      for (let x = 0; x <= grid.w; x++) {
        const on = x < grid.w && grid.cells[z * grid.w + x] === 1;
        if (on && start < 0) start = x;
        if (!on && start >= 0) { roof.push([grid.x0 + start, grid.z0 + z, grid.x0 + x, grid.z0 + z + 1]); start = -1; }
      }
    }
    this.extrude(g, segs, roof, y, h, storey, fill, line, storeyMat);
  }

  private extrude(
    g: THREE.Group, segs: [number, number, number, number][], roof: [number, number, number, number][], y: number, h: number, storey: number,
    fill: { wall: THREE.Material; roof: THREE.Material } | null, line: THREE.LineBasicMaterial | THREE.LineDashedMaterial, storeyMat: THREE.Material | null,
  ) {
    const top = y + h;
    if (fill) {
      // uv in blocks: the texture repeats once per block (walls: along the wall × down from the top)
      const mesh = (quads: [number[], number[], number[], number[], number[]][], mat: THREE.Material) => {
        const pos: number[] = [], uv: number[] = [], idx: number[] = [];
        for (const [a, b, c, d, t] of quads) {
          const n = pos.length / 3;
          pos.push(...a, ...b, ...c, ...d);
          uv.push(...t);
          idx.push(n, n + 1, n + 2, n, n + 2, n + 3);
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
        geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
        geo.setIndex(idx);
        g.add(new THREE.Mesh(geo, mat));
      };
      mesh(segs.map(([x0, z0, x1, z1]) => {
        const len = Math.hypot(x1 - x0, z1 - z0);
        return [[x0, y, z0], [x1, y, z1], [x1, top, z1], [x0, top, z0], [0, h, len, h, len, 0, 0, 0]];
      }), fill.wall);
      mesh(roof.map(([x0, z0, x1, z1]) => [[x0, top, z0], [x1, top, z0], [x1, top, z1], [x0, top, z1], [x0, z0, x1, z0, x1, z1, x0, z1]]), fill.roof);
    }
    const ring = (yy: number, out: number[]) => { for (const [x0, z0, x1, z1] of segs) out.push(x0, yy, z0, x1, yy, z1); };
    const edges: number[] = [];
    ring(y, edges);
    ring(top, edges);
    // vertical edges at the corners of the outline
    const corners = new Map<string, number>();
    for (const [x0, z0, x1, z1] of segs) for (const k of [`${x0},${z0}`, `${x1},${z1}`]) corners.set(k, (corners.get(k) ?? 0) + 1);
    for (const k of corners.keys()) { const [cx, cz] = k.split(",").map(Number); edges.push(cx, y, cz, cx, top, cz); }
    const eg = new THREE.BufferGeometry();
    eg.setAttribute("position", new THREE.Float32BufferAttribute(edges, 3));
    g.add(new THREE.LineSegments(eg, line));
    // storey lines
    if (storeyMat && storey > 0) {
      const fl: number[] = [];
      for (let fy = storey; fy < h; fy += storey) ring(y + fy, fl);
      if (fl.length) {
        const fg = new THREE.BufferGeometry();
        fg.setAttribute("position", new THREE.Float32BufferAttribute(fl, 3));
        g.add(new THREE.LineSegments(fg, storeyMat));
      }
    }
  }

  /** One quad on top of each block column (merged). */
  private cellMesh(cells: [number, number][], mat: THREE.Material, lift: number) {
    const list = cells.slice(0, 80000);
    const pos = new Float32Array(list.length * 12);
    const uv = new Float32Array(list.length * 8);
    const idx = new Uint32Array(list.length * 6);
    list.forEach(([x, z], n) => {
      const y = this.h(x + 0.5, z + 0.5) + lift;
      pos.set([x, y, z, x, y, z + 1, x + 1, y, z + 1, x + 1, y, z], n * 12);
      uv.set([0, 0, 0, 1, 1, 1, 1, 0], n * 8);
      const b = n * 4;
      idx.set([b, b + 1, b + 2, b, b + 2, b + 3], n * 6);
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
    geo.setIndex(new THREE.BufferAttribute(idx, 1));
    return new THREE.Mesh(geo, mat);
  }

  // --------- draft (tool preview) ----------

  /** Tool preview. For buildings `pts` is the outline so far (2 points = rectangle). */
  setDraft(kind: PlanObject["kind"] | null, pts: [number, number][], hover: [number, number] | null, height = 8) {
    dispose(this.draft, false);
    if (!kind) return;
    const all = hover ? [...pts, hover] : pts;
    const mat = new THREE.LineDashedMaterial({ color: 0xffffff, dashSize: 1, gapSize: 0.6, depthTest: false, transparent: true });
    if (kind === "building" && all.length >= 1) {
      const outline = all.length >= 3 ? all : all.length === 2 ? [all[0], [all[1][0], all[0][1]], all[1], [all[0][0], all[1][1]]] as [number, number][] : all;
      const b = bounds(outline);
      const y = this.h(b.cx, b.cz);
      const g = new THREE.Group();
      this.buildingShape(g, outline, y, height, 0, null, mat, null);
      g.traverse((c) => { if (c instanceof THREE.LineSegments) c.computeLineDistances(); });
      this.draft.add(g);
      const hm = new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false });
      for (const [x, z] of pts) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 0.5), hm);
        m.position.set(x + 0.5, y + 0.3, z + 0.5);
        m.renderOrder = 10;
        this.draft.add(m);
      }
      return;
    }
    if (all.length >= 2) {
      const ring = kind === "area" && all.length >= 3 ? [...all, all[0]] : all;
      const v = samplePolyline(ring.map(([x, z]) => [x + 0.5, z + 0.5]), 0.5).map(([x, z]) => new THREE.Vector3(x, this.h(x, z) + 0.3, z));
      const l = new THREE.Line(new THREE.BufferGeometry().setFromPoints(v), mat);
      l.computeLineDistances();
      this.draft.add(l);
    }
    const hm = new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false });
    for (const [x, z] of pts) {
      const m = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 0.5), hm);
      m.position.set(x + 0.5, this.h(x, z) + 0.3, z + 0.5);
      m.renderOrder = 10;
      this.draft.add(m);
    }
  }

  // --------- layout / template preview ----------

  get previewCount() {
    return this.preview.children.length;
  }

  setPreview(objs: PlanObject[] | null) {
    dispose(this.preview, false);
    if (!objs) return;
    for (const o of objs.slice(0, 3000)) {
      const { group } = this.build({ ...o, status: "idea" }, 0);
      // no name tags and not clickable
      group.traverse((c) => { delete c.userData.planId; });
      for (const c of [...group.children]) if (c instanceof CSS2DObject) { c.element.remove(); group.remove(c); }
      this.preview.add(group);
    }
  }

  // --------- home ----------

  setHome(h: { x: number; y: number; z: number } | null) {
    dispose(this.home, false);
    if (!h) return;
    const gold = new THREE.MeshBasicMaterial({ color: 0xf2c14e });
    const roof = new THREE.MeshBasicMaterial({ color: 0xc0392b });
    const body = new THREE.Mesh(new THREE.BoxGeometry(2, 1.6, 2), gold);
    body.position.set(h.x + 0.5, h.y + 4.8, h.z + 0.5);
    const top = new THREE.Mesh(new THREE.ConeGeometry(1.7, 1.4, 4), roof);
    top.rotation.y = Math.PI / 4;
    top.position.set(h.x + 0.5, h.y + 6.3, h.z + 0.5);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 4, 6), gold);
    pole.position.set(h.x + 0.5, h.y + 2, h.z + 0.5);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.9, 1.4, 24).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xf2c14e, transparent: true, opacity: 0.7, side: THREE.DoubleSide }));
    ring.position.set(h.x + 0.5, h.y + 0.08, h.z + 0.5);
    const div = document.createElement("div");
    div.className = "plan-label home-pin";
    div.innerHTML = ICON_SVG.home;
    div.append(" Home");
    const label = new CSS2DObject(div);
    label.position.set(h.x + 0.5, h.y + 8.5, h.z + 0.5);
    label.visible = this.showLabels;
    this.home.add(body, top, pole, ring, label);
  }

  // --------- collaborators ----------

  /**
   * Live cursors: an arrow in the person's colour (tip on the block they point at) with their name tag beside it.
   * Plain DOM (styled in ui/share.css; the layout is inline so the site's embed looks right without it). Always shown,
   * also with labels off: it's who's here, not a plan label.
   */
  setPeers(peers: Peer[]) {
    dispose(this.peers, false);
    for (const p of peers) {
      if (!p.cursor) continue;
      const div = document.createElement("div");
      div.className = "peer-cursor";
      div.setAttribute("aria-hidden", "true");
      div.style.setProperty("--c", p.color);
      div.style.pointerEvents = "none";
      // lucide mouse-pointer-2, its tip moved onto the point
      div.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" style="display:block;margin:-3.5px 0 0 -3px;overflow:visible"><path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" stroke="#0f1416" stroke-width="1.5" stroke-linejoin="round"/></svg>`;
      (div.firstChild as SVGElement).querySelector("path")!.setAttribute("fill", p.color);
      const tag = document.createElement("span");
      tag.className = `peer-label${darkInk(p.color) ? "" : " light"}`;
      tag.style.cssText = "position:absolute;left:11px;top:11px";
      tag.style.background = p.color;
      tag.textContent = p.name;
      div.append(tag);
      const l = new CSS2DObject(div);
      l.center.set(0, 0); // the element's top-left corner (= the arrow's tip) sits on the point
      l.userData.peer = true;
      l.position.set(p.cursor.x + 0.5, p.cursor.y + 1, p.cursor.z + 0.5);
      this.peers.add(l);
    }
  }
}

/** dark text on a light identity colour (most), light text on the few dark ones (old random cursor colours) */
function darkInk(hex: string) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
  if (!m) return true;
  const n = parseInt(m[1], 16);
  const lin = (c: number) => { const x = c / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255) >= 0.18;
}

function samplePolyline(pts: number[][], step: number): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, az] = pts[i], [bx, bz] = pts[i + 1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 1e-6) continue;
    const dx = (bx - ax) / len, dz = (bz - az) / len;
    const n = Math.max(1, Math.ceil(len / step));
    for (let k = 0; k < n; k++) out.push([ax + dx * (len * k) / n, az + dz * (len * k) / n, dx, dz]);
    if (i === pts.length - 2) out.push([bx, bz, dx, dz]);
  }
  if (!out.length && pts.length) out.push([pts[0][0], pts[0][1], 1, 0]);
  return out;
}

function dispose(g: THREE.Object3D, remove = true) {
  const kids = [...g.children];
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    m.geometry?.dispose();
    if (o instanceof CSS2DObject) o.element.remove();
  });
  if (remove) g.removeFromParent();
  else for (const k of kids) g.remove(k);
}
